import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, Drawer, Space, Row, Col, Tag } from 'antd';
import {
  WarningOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  InboxOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import ReportService from '../../../services/ReportService';
import StatCard from '../../../components/StatCard';
import ReportCompanySelect from '../../../components/ReportCompanySelect';
import ReportBranchSelect from '../../../components/ReportBranchSelect';
import { useReportScope } from '../../../hooks/useReportScope';

const CRITICAL_STOCK_STATUS = {
  URGENT: 'Acil',
  CRITICAL: 'Kritik',
  WARNING: 'Dikkat',
  NORMAL: 'Normal',
};

const STATUS_TAG_CLASS = {
  [CRITICAL_STOCK_STATUS.URGENT]: 'bg-red-600 text-white border-red-600',
  [CRITICAL_STOCK_STATUS.CRITICAL]: 'bg-red-500 text-white border-red-500',
  [CRITICAL_STOCK_STATUS.WARNING]: 'bg-amber-500 text-white border-amber-500',
  [CRITICAL_STOCK_STATUS.NORMAL]: 'bg-emerald-500 text-white border-emerald-500',
};

const CriticalStockReports = () => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);
  const [draftAdminCompanyCode, setDraftAdminCompanyCode] = useState(null);

  const [criticalStock, setCriticalStock] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  const [criticalStockLoading, setCriticalStockLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    token,
    allBranches,
    branchesLoading,
    companies,
    adminCompanyCode,
    setAdminCompanyCode,
    effectiveCompanyCode,
    needsCompanyPicker,
    getCompanyCodeForRequest,
  } = useReportScope();

  const fetchCriticalStock = async (branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    try {
      setCriticalStockLoading(true);

      if (!companyCode) {
        console.error('CompanyCode is required');
        setCriticalStock([]);
        setStockSummary(null);
        return;
      }

      const response = await ReportService.stock.getBranchCriticalStockReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
      });

      if (response.status === 200) {
        const raw = response.data?.data ?? response.data ?? {};
        const summary = raw.summary ?? null;
        const critical = Array.isArray(raw.critical)
          ? raw.critical
          : (Array.isArray(raw) ? raw : []);
        setStockSummary(summary);
        setCriticalStock(critical);
      } else {
        setCriticalStock([]);
        setStockSummary(null);
      }
    } catch (error) {
      console.error('Error fetching critical stock:', error);
      setCriticalStock([]);
      setStockSummary(null);
    } finally {
      setCriticalStockLoading(false);
    }
  };

  useEffect(() => {
    if (branchesLoading) {
      return;
    }
    if (!effectiveCompanyCode) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchCriticalStock();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [effectiveCompanyCode, branchesLoading]);

  const openFilters = () => {
    setDraftSelectedBranch(selectedBranch);
    setDraftAdminCompanyCode(adminCompanyCode);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedBranch(draftSelectedBranch);
    if (needsCompanyPicker) {
      setAdminCompanyCode(draftAdminCompanyCode);
    }
    setIsFilterOpen(false);
    fetchCriticalStock(draftSelectedBranch || null, draftAdminCompanyCode);
  };

  const clearFilters = () => {
    const defaultSelectedBranch = null;
    setDraftSelectedBranch(defaultSelectedBranch);
    setSelectedBranch(defaultSelectedBranch);
    fetchCriticalStock(null);
  };

  const formatNumber = (value, fractionDigits = 2) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(value);
  };

  const renderStatusBadge = (status) => {
    const label = status || CRITICAL_STOCK_STATUS.NORMAL;
    const className = STATUS_TAG_CLASS[label] || STATUS_TAG_CLASS[CRITICAL_STOCK_STATUS.NORMAL];
    return <Tag className={`${className} font-semibold`}>{label}</Tag>;
  };

  const renderTwoLineTitle = (firstWord, secondWord) => (
    isMobile ? (
      <span className="text-xs leading-tight">
        {firstWord}
        <br />
        {secondWord}
      </span>
    ) : (
      <span className="text-base">{firstWord} {secondWord}</span>
    )
  );

  const criticalStockColumns = [
    {
      title: renderTwoLineTitle('Stok', 'Adı'),
      dataIndex: 'StokAdi',
      key: 'StokAdi',
      width: isMobile ? '22%' : '18%',
      render: (text) => <span className="text-base font-semibold">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: renderTwoLineTitle('Şube', 'Adı'),
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '18%' : '14%',
      render: (text) => <span className="text-base">{text}</span>,
      ellipsis: isMobile
    },
    {
      title: isMobile ? (
        <span className="text-xs leading-tight">
          <span className="block whitespace-nowrap">Mevcut</span>
          <span className="block whitespace-nowrap">Bakiye</span>
        </span>
      ) : renderTwoLineTitle('Mevcut', 'Bakiye'),
      dataIndex: 'MevcutBakiye',
      key: 'MevcutBakiye',
      width: isMobile ? '16%' : '12%',
      render: (value, record) => {
        const bakiyeText = `${formatNumber(value || 0)} ${record.OlcuBirimi || ''}`;
        const ambalajText = record.AmbalajBirimi && record.DonusumMiktari
          ? ` (${formatNumber((value || 0) / (record.DonusumMiktari || 1))} ${record.AmbalajBirimi})`
          : '';
        return (
          <span className="text-base font-semibold text-red-600">
            {bakiyeText}{ambalajText}
          </span>
        );
      },
      sorter: isMobile ? false : (a, b) => (a.MevcutBakiye || 0) - (b.MevcutBakiye || 0),
    },
    {
      title: renderTwoLineTitle('Günlük', 'Tüketim'),
      dataIndex: 'GunlukOrtalamaTuketim',
      key: 'GunlukOrtalamaTuketim',
      width: isMobile ? '14%' : '11%',
      render: (value, record) => (
        <span className="text-base">
          {formatNumber(value || 0)} {record.OlcuBirimi || ''}
        </span>
      ),
      responsive: ['md'],
    },
    {
      title: renderTwoLineTitle('Lead', 'Time'),
      dataIndex: 'LeadTimeDays',
      key: 'LeadTimeDays',
      width: isMobile ? '10%' : '8%',
      render: (value) => <span className="text-base">{value ?? '-'}</span>,
      responsive: ['md'],
    },
    {
      title: renderTwoLineTitle('Min.', 'Stok'),
      dataIndex: 'OnerilenMinimumStok',
      key: 'OnerilenMinimumStok',
      width: isMobile ? '12%' : '10%',
      render: (value, record) => (
        <span className="text-base font-semibold text-orange-600">
          {formatNumber(value || 0)} {record.OlcuBirimi || ''}
        </span>
      ),
    },
    {
      title: renderTwoLineTitle('Kalan', 'Gün'),
      dataIndex: 'KalanGun',
      key: 'KalanGun',
      width: isMobile ? '10%' : '9%',
      render: (value) => (
        <span className="text-base font-semibold">
          {value != null ? formatNumber(value, 1) : '-'}
        </span>
      ),
      sorter: isMobile ? false : (a, b) => (a.KalanGun ?? 9999) - (b.KalanGun ?? 9999),
    },
    {
      title: 'Durum',
      dataIndex: 'Durum',
      key: 'Durum',
      width: isMobile ? '12%' : '10%',
      render: (status) => renderStatusBadge(status),
    },
    {
      title: renderTwoLineTitle('Stok', 'Tipi'),
      dataIndex: 'StokTipi',
      key: 'StokTipi',
      width: isMobile ? '12%' : '10%',
      render: (text) => <span className="text-base">{text}</span>,
      responsive: ['lg'],
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.StokKodu || '').toLowerCase().includes(needle) ||
      String(row.StokAdi || '').toLowerCase().includes(needle) ||
      String(row.StokTipi || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-orange-500/5 to-yellow-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg">
              <WarningOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Kritik Stok
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Kritik = önerilen minimum stok altı (son {stockSummary?.lookback_days ?? 30} gün çıkış tüketimine göre)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchCriticalStock(selectedBranch || null)}
              loading={criticalStockLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      <Row gutter={[12, 12]} className="mb-4">
        <Col xs={12} sm={12} lg={8}>
          <StatCard
            title="Takip Edilen Stok"
            value={stockSummary?.toplam_stok ?? 0}
            suffix="ürün"
            icon={<InboxOutlined />}
            gradientFrom="from-slate-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={12} sm={12} lg={8}>
          <StatCard
            title="Kritik Stok"
            value={stockSummary?.kritik_sayisi ?? 0}
            suffix="ürün"
            icon={<AlertOutlined />}
            gradientFrom="from-red-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
        <Col xs={24} sm={24} lg={8}>
          <StatCard
            title="Kritik Oran"
            value={stockSummary?.kritik_yuzde ?? 0}
            suffix="%"
            icon={<WarningOutlined />}
            gradientFrom="from-orange-100"
            gradientTo="to-white"
            isMobile={isMobile}
            compact
          />
        </Col>
      </Row>

      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube, stok kodu veya stok adı ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      <Card
        title={
          <div className="flex items-center gap-2">
            <WarningOutlined className="text-red-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              Kritik Stok Raporu
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        {criticalStock.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-base">Önerilen minimum stok altında ürün bulunmamaktadır.</p>
          </div>
        ) : (
          <Table
            columns={criticalStockColumns}
            dataSource={filterData(criticalStock)}
            loading={criticalStockLoading}
            rowKey={(record) => `${record.SubeKodu}-${record.StokKodu}`}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Toplam ${total} kritik stok`,
            }}
            size={isMobile ? 'small' : 'middle'}
            scroll={isMobile ? { x: 'max-content' } : undefined}
          />
        )}
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <ReportCompanySelect
            companies={companies}
            value={draftAdminCompanyCode}
            onChange={(value) => {
              setDraftAdminCompanyCode(value);
              setDraftSelectedBranch(null);
            }}
            loading={branchesLoading}
          />

          <ReportBranchSelect
            allBranches={allBranches}
            needsCompanyPicker={needsCompanyPicker}
            draftAdminCompanyCode={draftAdminCompanyCode}
            adminCompanyCode={adminCompanyCode}
            effectiveCompanyCode={effectiveCompanyCode}
            value={draftSelectedBranch}
            onChange={setDraftSelectedBranch}
            loading={branchesLoading}
          />

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default CriticalStockReports;
